import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck, MapPin, Wrench, Receipt, Users, Shield,
  Check, ArrowRight, Fuel, Calculator, TrendingUp, Info,
  Plus, Minus, Sparkles
} from 'lucide-react';
import {
  PLAN_CONFIG,
  computeQuote,
  estimateAnnualSavings,
  VEHICLE_TIERS,
} from '../../utils/pricing';

// Modules affichables (dans l'ordre stratégique)
const AVAILABLE_MODULES = [
  { id: 'localisation', label: 'Localisation', icon: MapPin, color: 'blue', desc: 'GPS temps réel + suivi 24/7' },
  { id: 'repartition', label: 'Répartition', icon: Truck, color: 'amber', desc: 'Kanban dispatch + assignation' },
  { id: 'entretien', label: 'Entretien', icon: Wrench, color: 'emerald', desc: 'SAAQ + freins/pneus' },
  { id: 'facturation', label: 'Facturation', icon: Receipt, color: 'purple', desc: 'Factures auto + scanner IA' },
  { id: 'depenses', label: 'Gestion dépenses', icon: Calculator, color: 'cyan', desc: 'Suivi par chauffeur/véh' },
  { id: 'logistique_plus', label: 'Logistique Plus', icon: Users, color: 'pink', desc: 'Workflow multi-niveaux SAC' },
];

const HOURS_PER_VEHICLE_WEEK = { localisation: 0.25, repartition: 0.3, entretien: 0.15, facturation: 0.3, depenses: 0.15, logistique_plus: 0.2 };
const LEISURE = ['avec vos proches', 'à la pêche', 'au golf'];

const TimeBack = ({ modules, vehicles, savings }) => {
  const raw = modules.reduce((h, m) => h + (HOURS_PER_VEHICLE_WEEK[m] || 0), 0) * vehicles;
  const hours = Math.max(1, Math.round(raw * 2) / 2);
  return (
    <div className="bg-slate-800/40 rounded-xl p-4 mb-4" data-testid="quote-time-back">
      <p className="text-sm text-slate-300 leading-relaxed">
        Pourrait vous faire gagner environ <span className="text-amber-400 font-bold text-lg" data-testid="quote-hours">{hours.toLocaleString('fr-CA')} h</span> par semaine
        {' '}{LEISURE.join(', ').replace(/, ([^,]*)$/, ' ou $1')}.
      </p>
      <p className="text-[10px] text-slate-500 mt-1">Exemple à titre indicatif, pas une promesse : dépend de votre réalité.</p>
      <p className="text-[11px] text-slate-500 mt-3 pt-3 border-t border-slate-700/60" data-testid="quote-operational-value">
        Valeur opérationnelle estimée : <span className="text-slate-300 font-semibold">{savings.total.toLocaleString('fr-CA')} $/an</span>
        <span className="block text-[10px] mt-0.5">Carburant, administration et conformité (~1 080 $/an par véhicule)</span>
      </p>
    </div>
  );
};

export const QuoteCalculator = ({ story = false }) => {
  const navigate = useNavigate();
  const [selectedModules, setSelectedModules] = useState(['localisation', 'repartition']);
  const [vehicles, setVehicles] = useState(10);
  const [yearly, setYearly] = useState(false);
  const [lpParams, setLpParams] = useState({
    dispatchers: 3,
    salesAgents: 5,
    ordersPerMonth: 1500,
  });

  const toggleModule = (id) => {
    setSelectedModules(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const quote = useMemo(() =>
    computeQuote({
      selectedModules,
      vehicles,
      logistiquePlusParams: lpParams,
      yearly,
    }),
    [selectedModules, vehicles, yearly, lpParams]
  );

  const savings = useMemo(() => estimateAnnualSavings(vehicles), [vehicles]);
  const displayPrice = yearly ? quote.yearly.net : quote.monthly * 12;
  const monthlyEquivalent = yearly ? quote.monthly - (quote.monthly * 2 / 12) : quote.monthly;
  const showLogistiquePlus = selectedModules.includes('logistique_plus');

  return (
    <section className={story ? '' : 'py-20 px-4 sm:px-8 bg-gradient-to-b from-slate-950 to-slate-900'} data-testid="quote-calculator">
      <div className="max-w-7xl mx-auto">
        {!story && <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
            <Sparkles size={14} />
            Devis instantané personnalisé
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-3">
            Construisez votre forfait sur mesure
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Sélectionnez vos modules, ajustez votre flotte, et voyez le prix se calculer en temps réel avec vos escomptes de volume.
          </p>
        </div>}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* CONFIGURATION à gauche */}
          <div className="lg:col-span-3 space-y-6">
            {/* Étape 1: Modules */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-amber-500 text-black rounded-full flex items-center justify-center font-bold text-sm">1</div>
                <h3 className="text-lg font-bold text-white">{story ? 'Choisissez vos outils' : 'Choisissez vos modules'}</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {AVAILABLE_MODULES.map(mod => {
                  const isSelected = selectedModules.includes(mod.id);
                  const isIncluded = quote.effectivelyIncluded.includes(mod.id);
                  const cfg = PLAN_CONFIG[mod.id];
                  const Icon = mod.icon;
                  return (
                    <button
                      key={mod.id}
                      onClick={() => !isIncluded && toggleModule(mod.id)}
                      disabled={isIncluded}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${
                        isIncluded
                          ? 'bg-emerald-500/10 border-emerald-500/40 cursor-not-allowed opacity-70'
                          : isSelected
                            ? 'bg-amber-500/15 border-amber-500 shadow-lg shadow-amber-500/10'
                            : 'bg-slate-800/40 border-slate-700 hover:border-slate-500'
                      }`}
                      data-testid={`module-toggle-${mod.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                          isSelected || isIncluded ? 'bg-amber-500/20' : 'bg-slate-700/50'
                        }`}>
                          <Icon size={18} className={
                            isIncluded ? 'text-emerald-400' :
                            isSelected ? 'text-amber-400' : 'text-slate-400'
                          } />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-bold text-white text-sm">{mod.label}</span>
                            {cfg && (
                              <span className="text-xs text-slate-400 shrink-0">
                                {cfg.basePrice === 0 ? 'Gratuit' : `${cfg.basePrice}$`}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{mod.desc}</p>
                          {isIncluded && (
                            <span className="text-[10px] text-emerald-400 font-bold mt-1 inline-block">
                              ✓ INCLUS AVEC UN AUTRE PLAN
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Étape 2: Véhicules */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-amber-500 text-black rounded-full flex items-center justify-center font-bold text-sm">2</div>
                <h3 className="text-lg font-bold text-white">Combien de véhicules ?</h3>
              </div>
              <div className="flex items-center gap-4 mb-3">
                <button
                  onClick={() => setVehicles(Math.max(1, vehicles - 1))}
                  className="w-10 h-10 rounded-lg bg-slate-700 hover:bg-slate-600 text-white flex items-center justify-center"
                  data-testid="vehicles-minus"
                >
                  <Minus size={16} />
                </button>
                <div className="flex-1 text-center">
                  <span className="text-4xl font-black text-amber-400" data-testid="vehicles-count">{vehicles}</span>
                  <span className="text-slate-400 text-sm ml-2">véhicule{vehicles > 1 ? 's' : ''}</span>
                </div>
                <button
                  onClick={() => setVehicles(Math.min(200, vehicles + 1))}
                  className="w-10 h-10 rounded-lg bg-slate-700 hover:bg-slate-600 text-white flex items-center justify-center"
                  data-testid="vehicles-plus"
                >
                  <Plus size={16} />
                </button>
              </div>
              <input
                type="range" min="1" max="100" value={vehicles}
                onChange={e => setVehicles(parseInt(e.target.value))}
                className="w-full h-2 bg-slate-700 rounded-full appearance-none cursor-pointer accent-amber-500"
                data-testid="vehicles-slider"
              />
              <div className="mt-3 text-xs text-slate-500 bg-slate-800/50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2 text-amber-400 font-semibold">
                  <Info size={12} />
                  Escomptes dégressifs par volume
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                  {VEHICLE_TIERS.map((t, i) => (
                    <div key={i} className={`p-1.5 rounded ${
                      (vehicles - 5) > t.from ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-800'
                    }`}>
                      <div className="text-white font-bold text-xs">
                        {t.pricePerVehicle.toFixed(2)}$
                      </div>
                      <div className="text-[9px] text-slate-500 mt-0.5">
                        {t.to === Infinity ? `${t.from}e +` : `${t.from}-${t.to}e`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Étape 3: Logistique Plus (conditionnel) */}
            {showLogistiquePlus && (
              <div className="bg-pink-500/5 border border-pink-500/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                  <h3 className="text-lg font-bold text-white">Logistique Plus - Détails</h3>
                </div>
                <div className="space-y-4">
                  <NumberField
                    label="Nombre de répartiteurs"
                    value={lpParams.dispatchers}
                    onChange={v => setLpParams({ ...lpParams, dispatchers: v })}
                    min={0} max={50}
                    hint="Prix dégressif : 25$/25$/20$/20$/20$/15$..."
                    testid="lp-dispatchers"
                  />
                  <NumberField
                    label="Nombre d'agents ventes"
                    value={lpParams.salesAgents}
                    onChange={v => setLpParams({ ...lpParams, salesAgents: v })}
                    min={0} max={30}
                    hint="3 inclus. Chaque supplémentaire = 5$/mois."
                    testid="lp-agents"
                  />
                  <NumberField
                    label="Commandes par mois"
                    value={lpParams.ordersPerMonth}
                    onChange={v => setLpParams({ ...lpParams, ordersPerMonth: v })}
                    min={0} max={20000} step={100}
                    hint="1 000 incluses. Chaque supplémentaire = 0,02$."
                    testid="lp-orders"
                  />
                </div>
              </div>
            )}
          </div>

          {/* SOMMAIRE à droite (sticky) */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-b from-slate-900/90 to-slate-950 border-2 border-amber-500/30 rounded-2xl p-6 lg:sticky lg:top-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Votre devis</h3>
                <div className="flex items-center gap-2 bg-slate-800 rounded-full p-1">
                  <button
                    onClick={() => setYearly(false)}
                    className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                      !yearly ? 'bg-amber-500 text-black' : 'text-slate-400'
                    }`}
                    data-testid="billing-monthly"
                  >Mensuel</button>
                  <button
                    onClick={() => setYearly(true)}
                    className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                      yearly ? 'bg-amber-500 text-black' : 'text-slate-400'
                    }`}
                    data-testid="billing-yearly"
                  >
                    Annuel <span className="text-[9px] opacity-80">-16%</span>
                  </button>
                </div>
              </div>

              {/* Lignes détaillées */}
              <div className="space-y-2 mb-4">
                {quote.lines.map((line, i) => (
                  <div key={i} className="flex items-start justify-between text-sm border-b border-slate-800 pb-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold">{line.module}</div>
                      <div className="text-[11px] text-slate-500">
                        Base: {line.base.toFixed(2)}$
                        {line.vehicleCost > 0 && ` + Véh: ${line.vehicleCost.toFixed(2)}$`}
                        {line.details && (
                          <>
                            <br />
                            Répartiteurs: {line.details.dispatcherCost.toFixed(2)}$
                            {line.details.agentsCost > 0 && ` + Agents: ${line.details.agentsCost.toFixed(2)}$`}
                            {line.details.ordersCost > 0 && ` + Volume: ${line.details.ordersCost.toFixed(2)}$`}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-white font-bold shrink-0 ml-2">
                      {line.subtotal.toFixed(2)}$
                    </div>
                  </div>
                ))}
                {quote.lines.length === 0 && (
                  <div className="text-center text-slate-500 text-sm py-4">
                    Sélectionnez au moins un module pour voir votre devis.
                  </div>
                )}
              </div>

              {/* Bundle discount */}
              {quote.bundleDiscount > 0 && (
                <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles size={14} className="text-emerald-400" />
                    <span className="text-sm text-emerald-300 font-semibold">
                      Escompte bundle ({(quote.bundleDiscount * 100).toFixed(0)}%)
                    </span>
                  </div>
                  <span className="text-emerald-300 font-bold" data-testid="bundle-saving">
                    -{quote.bundleSaving.toFixed(2)}$
                  </span>
                </div>
              )}

              {/* Total */}
              <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 mb-4">
                <div className="text-xs text-amber-400 font-semibold uppercase tracking-widest mb-1">
                  Total {yearly ? 'annuel' : 'mensuel'}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-black text-white" data-testid="quote-total">
                    {(yearly ? quote.yearly.net : quote.monthly).toFixed(2)}$
                  </span>
                  <span className="text-slate-400 text-sm">/{yearly ? 'an' : 'mois'}</span>
                </div>
                {yearly && quote.yearly.saving > 0 && (
                  <div className="text-xs text-emerald-400 mt-1">
                    ✓ Vous économisez {quote.yearly.saving.toFixed(2)}$/an (2 mois offerts)
                  </div>
                )}
                {!yearly && (
                  <div className="text-xs text-slate-500 mt-1">
                    ≈ {(quote.monthly * 12).toFixed(2)}$/an
                  </div>
                )}
              </div>

              {/* ROI */}
              {story ? <TimeBack modules={quote.lines.length ? [...new Set([...selectedModules, ...quote.effectivelyIncluded])] : []} vehicles={vehicles} savings={savings} /> : (
              <div className="bg-slate-800/40 rounded-xl p-3 mb-4 text-xs">
                <div className="flex items-center gap-2 text-emerald-400 font-semibold mb-1">
                  <TrendingUp size={13} />
                  Économies annuelles estimées
                </div>
                <div className="text-slate-300">
                  <span className="text-white font-bold text-lg">{savings.total.toLocaleString('fr-CA')}$</span>
                  <span className="text-slate-500 ml-1">/an</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  Carburant + admin + conformité (~1 080$/an par véhicule).
                </div>
              </div>
              )}

              <button
                onClick={() => navigate('/admin/register')}
                className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 rounded-xl transition-colors text-sm"
                data-testid="quote-cta-btn"
              >
                Commencer l'essai gratuit 30 jours <ArrowRight size={16} />
              </button>
              <p className="text-[10px] text-slate-500 text-center mt-2">
                Sans engagement · Sans carte de crédit · Devis modifiable à tout moment
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const NumberField = ({ label, value, onChange, min = 0, max = 100, step = 1, hint, testid }) => (
  <div>
    <label className="flex items-center justify-between text-sm text-slate-300 mb-2">
      <span>{label}</span>
      <span className="text-xl font-bold text-white" data-testid={`${testid}-value`}>{value}</span>
    </label>
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(parseInt(e.target.value))}
      className="w-full h-2 bg-slate-800 rounded-full appearance-none cursor-pointer accent-pink-500"
      data-testid={testid}
    />
    {hint && <p className="text-[10px] text-slate-500 mt-1">{hint}</p>}
  </div>
);

export default QuoteCalculator;
